import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { ReportFormPage } from 'src/components/shared/report/ReportFormPage';
import { RootState } from 'src/features';
import { actions } from 'src/features/resort';
import { getQueryParam } from 'src/lib/utils';
import { ReportCategory } from 'src/models/ReportCategory';

interface ResortReportFormProps {
  basePath: string;
}

export const ResortReportForm: React.FC<ResortReportFormProps> = ({ basePath }) => {
  const { t, i18n } = useTranslation('best_friends');
  const history = useHistory();
  const location = useLocation();
  const { unlockFlags } = useSelector((state: RootState) => state.user);
  const { reportStatus, displayedUserProfile } = useSelector((state: RootState) => state.resort);
  const dispatch = useDispatch();

  const resortId = getQueryParam(location.search, 'resort-id');
  const isFollowing = getQueryParam(location.search, 'follow') === 'true';
  const isUnlocked = unlockFlags?.isUnlockedResortAppNet ? 'true' : 'false';
  const language = i18n.language;

  useEffect(() => {
    switch (reportStatus?.state) {
      case 'ok':
        history.replace(
          `${basePath}/report-succeeded?is-following=${isFollowing}&is-unlocked=${isUnlocked}`
        );
        break;
      case 'error':
        history.push(`${basePath}/report/report-failed`);
        break;
    }
    // isFollowingは遷移によって状態が変わってしまうため、第2引数に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, reportStatus, basePath]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetReportStatus());
    };
  }, [dispatch]);

  const handleSubmit = useCallback(
    (category: ReportCategory, reason: string) => {
      if (displayedUserProfile?.state !== 'ok') {
        return;
      }
      const profile = displayedUserProfile.result;
      let type: 'resort' | 'user_profile';
      let contentId: string;
      let digest: string;
      let createdAt: number;

      if (resortId) {
        const found = profile.constructions.find(c => c.resortId === resortId);
        if (!found) {
          return;
        }
        type = 'resort';
        contentId = resortId;
        digest = found.digest;
        createdAt = found.createdAt;
      } else {
        type = 'user_profile';
        contentId = profile.contentId;
        digest = profile.digest;
        createdAt = profile.createdAt;
      }

      dispatch(
        actions.report({
          type,
          category,
          language,
          reason,
          ugc: { contentId, digest, createdAt },
          isFollowing,
          raid: profile.resortPlannerId
        })
      );
    },
    [dispatch, displayedUserProfile, resortId, language, isFollowing]
  );

  return (
    <ReportFormPage
      title={t('report_spam')}
      backButtonLabel={t('aria_button_back')}
      onSubmit={handleSubmit}
      isLoading={reportStatus?.state === 'pending'}
    />
  );
};
